'use client';
import { useAuthStore } from '@/app/store/use-auth-store';
import { Button } from '@/shared/ui/button';
import { CField } from '@/shared/ui/cfield';
import CPhoneInput from '@/shared/ui/phone-input';
import { isPhoneValid } from '@/shared/ui/phone-input/isPhoneValid';
import { Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { FormEvent, useState } from 'react';
import { useAuthFlowStore } from '../../model/use-auth-flow-store';
import { useCheckUser, useLoginWithoutOTP } from '../api/query';

type Props = {
  onSuccess: () => void;
};

export const LoginForm: React.FC<Props> = ({ onSuccess }) => {
  const t = useTranslations();
  const { changeStep, payload, setAuthMode, setPayload } = useAuthFlowStore();
  const { setUser } = useAuthStore();

  const [phoneError, setPhoneError] = useState(false);
  const [checkValidLoading, setCheckValidLoading] = useState(false);
  const [phone, setPhone] = useState(payload.phone);

  const { mutate: checkUser, isPending } = useCheckUser({
    onSuccess: () => {
      setAuthMode('login');
      loginWithoutOtp({ phone });
    },

    onError: () => {
      setPayload({ phone });
      setAuthMode('register');
      changeStep('register');
    },
  });

  const { mutate: loginWithoutOtp } = useLoginWithoutOTP({
    onSuccess: (data) => {
      onSuccess();
      setUser(data);
    },
  });

  // submit phone number
  const onLogin = async (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();

    setCheckValidLoading(true);
    const isValid = await isPhoneValid(phone);
    setCheckValidLoading(false);

    if (isValid) {
      checkUser({ phone });
    } else {
      setPhoneError(true);
    }
  };

  return (
    <Stack as="form" flex="1" onSubmit={(e) => onLogin(e)} gap={4}>
      <Text color={'text'} fontSize="2xl" fontWeight={700} mb={2}>
        {t('login')}
      </Text>
      <CField
        invalid={phoneError || false}
        errorText={
          phoneError ? t('incorrect_phone_number') : t('error_occured')
        }
      >
        <CPhoneInput
          label={t('phone_number')}
          value={phone}
          onChange={(e) => {
            if (typeof e === 'string') {
              setPhoneError(false);
              setPhone(e);
            }
          }}
        />
      </CField>
      <Text color={'text'} fontSize="sm" textAlign={'start'}>
        {t('permission_privacy_policy')}{' '}
        <Text as="span" color="primary">
          {t('terms_of_use')}
        </Text>
      </Text>
      <Button
        type="submit"
        fontSize="md"
        height="56px"
        bg="primary"
        color="primary.text"
        _hover={{
          bg: 'primary.hover',
        }}
        loading={isPending || checkValidLoading}
      >
        {t('login')}
      </Button>
    </Stack>
  );
};
